import { Box, Button, CircularProgress, FormControl, IconButton, InputAdornment, Menu, MenuItem, Select, SelectChangeEvent, styled, TextField, Tooltip, Typography, } from '@mui/material';
import { DataGridPro, GridFilterModel } from '@mui/x-data-grid-pro'
import React, { useState, useEffect, useCallback, useMemo, useRef, SetStateAction, Dispatch } from 'react'
import { useGridPrivateApiContext } from '@mui/x-data-grid/internals';
import { DataGridProProps, GridColumnResizeParams, GridRowParams, GridSlotsComponentsProps, GridToolbarColumnsButton, GridToolbarContainer, renderActionsCell } from '@mui/x-data-grid-pro';
import { Clear, Search, PersonAdd, Download, FileDownloadOutlined } from '@mui/icons-material';
import { LicenseInfo } from '@mui/x-license';
import { textColor } from '../../theme/palette';
import './table.css';
import { formatColumns } from '../../utils/date-time-type';
import { mapReactFilterValueTypeToRequestFilterValueType, mapReactMUIToFilterOperationTypes, RequestFilterOperationTypeEnum, RequestFilterValueTypeEnums } from '../../utils/RequestFilterEnums';
import { customLocaleText } from '../../utils/table-turkish-translate';
import { normalizeString } from '../../utils/normalizeString';
import { downloadExcel, refactorData } from '../../utils/downloadExcel';
import { useGetAllDataMutation } from '../services/services';
import { enqueueSnackbar } from 'notistack';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
const key: string = process.env.REACT_APP_MUI_KEY || '';
LicenseInfo.setLicenseKey(key);

/** Hücre kopyalanırken kullanılacak değeri üretir: tarih TR formatında, sayı/para binlik ayraçsız virgüllü (123123,12). */
function getValueForClipboard(params: { value?: any; formattedValue?: any; colDef?: { type?: string } }): string {
    const type = params.colDef?.type;
    const v = params.value;
    if (v == null || v === '') return '';

    if (type === 'datetime' || type === 'date') {
        const d = typeof v === 'object' && v instanceof Date ? v : new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        if (type === 'date') return d.toLocaleDateString('tr-TR');
        return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }

    if (type === 'number' || type === 'decimal' || type === 'decimalOnly' || type === 'decimalPercentage') {
        const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/\./g, '').replace(',', '.'));
        if (Number.isNaN(num)) return String(params.formattedValue ?? v);
        return String(num).replace('.', ',');
    }

    return String(params.formattedValue ?? v);
}

export type TableProps = DataGridProProps<any> & {
    openDialog?: any;
    components?: GridSlotsComponentsProps;
    onDownload?: () => void;
    exportFileName?: string;
    onSave?: () => any;
    isLoading?: boolean;
    isMasterDetailActive?: boolean;
    masterDetailContent?: ((params: GridRowParams<any>) => React.ReactNode) | undefined;
    masterDetailHeight?: ((params: GridRowParams<any>) => number | "auto") | undefined;
    isRowExpandable?: boolean;
    goToAddPage?: { func: () => void, title: string, text: string };
    goToAddPage2?: { func: () => void, title: string, text: string };
    onPaginationModelChange: any,
    sortModelChange: any,
    headerFilters: boolean,
    columnVisibilityConfigKey: string,
    filterKey: string,
    handleFilterChange: (filterObj: any) => void;
    setColumnVisibilityModel: Dispatch<SetStateAction<Record<string, boolean>>>;
    columnVisibilityModel: Record<string, boolean>;
    idName?: string;
    listUrl: string;
    downloadUrl?: string;
    fetchData: any | undefined;
    tabTableName?: string;
    customerId?: any;
    companyId?: any;
    rowCount?: number;
    customFilter?: any
    filterControl?: any
    filterBtnRef?: any
    requestBody?: any
    setRemoveFilter?: any
    noClear?: boolean;
    fetchMethod?: string; // GET veya POST
    control?: boolean;
    onEditClick?: () => void;
    hasActiveFilters?: boolean;
    rows?: any;
    closeTableHeaderName?: string;
    showAddUserButton?: boolean;
    onAddUserClick?: () => void;
    showDownloadButton?: boolean;
    disabledDownload?: boolean;
    listName?: string;
    columns: any[];
    extraButtons?: any;
    excelParams?: any;
    resetFilters?: boolean;
};
 
const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    '& .ant-empty-img-1': {
        fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
    },
    '& .ant-empty-img-2': {
        fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
    },
    '& .ant-empty-img-3': {
        fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
    },
    '& .ant-empty-img-4': {
        fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
    },
    '& .ant-empty-img-5': {
        fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
        fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
    },
}));
 
const nullComponent = () => null;

/** singleSelect header filter için: groupBy seçenekleri ve fetch fonksiyonu. */
type GroupByState = { items: { key?: string | number; count?: number }[]; loading: boolean; error: any };

/**
 * Backend groupBy response'undan options üretir.
 * { key, count }[] → { value, label }[]. Lookup varsa label oradan, yoksa "key (count)" veya "key".
 */
function mapGroupByToOptions(
    items: { key?: string | number; value?: string | number; count?: number }[],
    valueOptions: any[]
): { value: string | number; label: string }[] {
    if (!Array.isArray(items) || items.length === 0) return [];
    const lookup = new Map<string, string>();
    (valueOptions ?? []).forEach((opt: any) => {
        const val = opt?.value ?? opt;
        const label = opt?.label ?? opt?.text ?? String(val);
        lookup.set(String(val), label);
    });
    const NULL_OPTION_VALUE = '__null__';
    const seenNull = { current: false };
    return items
        .map((r: any) => {
            const key = r?.key ?? r?.value;
            const isEmptyKey = key === undefined || key === null || key === '';
            if (isEmptyKey) {
                if (seenNull.current) return null;
                seenNull.current = true;
                const label = r?.count != null ? `Boş Olanlar (${r.count})` : 'Boş Olanlar';
                return { value: NULL_OPTION_VALUE, label };
            }
            if (typeof key === 'object') return null;
            const keyStr = String(key);
            if (keyStr === '[object Object]') return null;
            const label = lookup.get(keyStr) ?? (r?.count != null ? `${keyStr} (${r.count})` : keyStr);
            return { value: key, label };
        })
        .filter((x): x is { value: string | number; label: string } => x != null && String(x.label).trim() !== '');
}

/**
 * singleSelect kolonları için dinamik header filter.
 * Select açıldığında groupBy ile backend'den seçenekleri çeker, lookup ile label'ları eşleştirir.
 */
function DynamicSingleSelectHeaderFilter(props: {
    colDef: any;
    item: any;
    inputRef: React.RefObject<unknown>;
    valueOptions: any[];
    groupByOptions: Record<string, GroupByState>;
    fetchGroupByOptions: (field: string) => void;
}) {
    const { colDef, item, inputRef, valueOptions, groupByOptions, fetchGroupByOptions: onFetchGroupBy } = props;
    const apiRef = useGridPrivateApiContext();
    const field = colDef?.field;
    const rawValue = item?.value;
    const filterValue =
        rawValue === null || rawValue === '__null__'
            ? '__null__'
            : rawValue === undefined || typeof rawValue === 'object' || String(rawValue) === '[object Object]'
                ? ''
                : rawValue;

    const safeValueOpts = Array.isArray(valueOptions) ? valueOptions : [];
    const opts = groupByOptions[field] ?? { items: [], loading: false, error: null };
    const options = useMemo(() => {
        const items = opts?.items;
        if (Array.isArray(items) && items.length > 0) {
            return mapGroupByToOptions(items, safeValueOpts);
        }
        // Response data boş geldiyse lookup seçenekleri yerine boş döndür (aşağıda "Kayıt yok" gösterilir)
        return [];
    }, [opts?.items, safeValueOpts]);
    // Değer varsa her zaman göster (sayfa yenilendiğinde options henüz yüklenmemiş olsa bile). __null__ = "Boş Olanlar".
    const selectValue = useMemo(() => {
        if (filterValue === '') return '';
        return filterValue;
    }, [filterValue]);
    const handleChange = useCallback(
        (e: SelectChangeEvent<string | number>) => {
            const value = e.target.value;
            // Filtre modelinde null saklanınca onFilterChange tarafından siliniyor; "Boş Olanlar" için '__null__' kullanıyoruz.
            const filterItemValue = value === '' ? undefined : value === '__null__' ? '__null__' : value;
            apiRef?.current?.upsertFilterItem?.({ ...item, value: filterItemValue });
        },
        [apiRef, item]
    );
    const handleOpen = useCallback(() => onFetchGroupBy(field), [field, onFetchGroupBy]);
    const isLoading = opts?.loading === true;

    const autoFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (autoFetchedRef.current) return;
        if (filterValue && filterValue !== '__null__' && !opts?.items?.length && !opts?.loading) {
            autoFetchedRef.current = true;
            onFetchGroupBy(field);
        }
    }, [field, filterValue, opts?.items?.length, opts?.loading, onFetchGroupBy]);

    const hasValueWaitingForLabel = filterValue && filterValue !== '' && filterValue !== '__null__' && options.length === 0 && !opts?.loading;
    const hasActiveFilter = filterValue !== '' && filterValue !== undefined;

    const handleClose = useCallback(() => {
        const api = apiRef as React.MutableRefObject<any>;
        const headersEl = api?.current?.columnHeadersContainerRef?.current;
        const virtualEl = api?.current?.virtualScrollerRef?.current;
        const scrollLeft = virtualEl?.scrollLeft ?? headersEl?.scrollLeft ?? 0;
        const scrollTop = virtualEl?.scrollTop ?? 0;
        (document.activeElement as HTMLElement)?.blur?.();
        if (api?.current?.setState) {
            api.current.setState((state: any) => ({
                ...state,
                focus: { ...state.focus, cell: null, columnHeader: null, columnHeaderFilter: null, columnGroupHeader: null },
            }));
            api.current.forceUpdate?.();
        }
        const restoreScroll = () => {
            if (headersEl) headersEl.scrollLeft = scrollLeft;
            if (virtualEl) virtualEl.scrollTo?.({ left: scrollLeft, top: scrollTop });
        };
        requestAnimationFrame(restoreScroll);
        setTimeout(restoreScroll, 50);
    }, [apiRef]);

    const handleClearFilter = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            apiRef?.current?.upsertFilterItem?.({ ...item, value: undefined });
        },
        [apiRef, item]
    );

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                // Tarih input clear butonu ile aynı davranış: masaüstünde hover/focus'ta X görünsün
                '& .groupByClearButton': {
                    opacity: 1,
                },
                '@media (pointer: fine)': {
                    '& .groupByClearButton': {
                        opacity: 0,
                    },
                    '&:hover .groupByClearButton, &:focus-within .groupByClearButton': {
                        opacity: 1,
                    },
                },
            }}
        >
            <FormControl fullWidth size="small" sx={{ minWidth: 80 }}>
                <Select
                    inputRef={inputRef as any}
                    value={selectValue}
                    onChange={handleChange}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    displayEmpty
                    size="small"
                    disabled={isLoading}
                    MenuProps={{ disableRestoreFocus: true }}
                    sx={{
                        fontSize: '0.875rem',
                        height: 36,
                        pr: hasActiveFilter ? 4.5 : 1.5,
                        '& .MuiSelect-icon': {
                            // Ok ikonu en sağda, X onun hemen solunda
                            right: hasActiveFilter ? 8 : 14,
                        },
                    }}
                    renderValue={(v) => {
                        if (isLoading || hasValueWaitingForLabel) {
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                                    <span>Yükleniyor...</span>
                                </Box>
                            );
                        }
                        if (v === '' || (v != null && (typeof v === 'object') || String(v) === '[object Object]')) return '';
                        if (v === '__null__' || v === null) return 'Boş Olanlar';
                        const label = options.find((o) => o.value === v || String(o.value) === String(v))?.label;
                        return label ?? String(v);
                    }}
                >
                    {isLoading ? (
                        <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                <CircularProgress size={16} />
                                <span>Seçenekler yükleniyor...</span>
                            </Box>
                        </MenuItem>
                    ) : options.length === 0 ? (
                        <MenuItem disabled>
                            <em>Kayıt yok</em>
                        </MenuItem>
                    ) : (
                        options.map((opt) => (
                            <MenuItem key={String(opt.value)} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>
            {hasActiveFilter && (
                <IconButton
                    className="groupByClearButton"
                    size="small"
                    onClick={handleClearFilter}
                    aria-label="Filtreyi temizle"
                    sx={{
                        position: 'absolute',
                        // X okün solunda dursun
                        right: 32,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: 0.5,
                        color: 'action.disabled',
                        transition: 'opacity 0.15s, color 0.15s',
                        '&:hover': {
                            color: 'text.secondary',
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <Clear fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
}
 
function CustomNoRowsOverlay() {
    return (
        <StyledGridOverlay sx={{ position: 'relative', zIndex: 0 }}>
            <svg
                width="150"
                height="150"
                viewBox="0 0 184 152"
                focusable="false"
            >
                <g fill="none" fillRule="evenodd">
                    <g transform="translate(24 31.67)">
                        <ellipse
                            className="ant-empty-img-5"
                            cx="67.797"
                            cy="106.89"
                            rx="67.797"
                            ry="12.668"
                        />
                        <path
                            className="ant-empty-img-1"
                            d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                        />
                        <path
                            className="ant-empty-img-2"
                            d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                        />
                        <path
                            className="ant-empty-img-3"
                            d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                        />
                    </g>
                    <path
                        className="ant-empty-img-3"
                        d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                    />
                    <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                        <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                        <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                    </g>
                </g>
            </svg>
            <Typography
                sx={{ mt: 2, fontSize: 14, fontWeight: 400, color: '#888', textAlign: 'center' }}
                variant="body2"
            >
                Kayıt Bulunamadı
            </Typography>
        </StyledGridOverlay>
    );
}
 
 
const Table = ({
    requestBody,
    columns,
    filterKey,
    handleFilterChange,
    openDialog,
    columnVisibilityConfigKey,
    setColumnVisibilityModel,
    hasActiveFilters,
    filterMode = 'server',
    isMasterDetailActive = false,
    isLoading = false,
    rows,
    getRowId,
    goToAddPage,
    goToAddPage2,
    checkboxSelection,
    tabTableName,
    closeTableHeaderName,
    idName,
    columnVisibilityModel,
    onPaginationModelChange,
    sortModelChange,
    listUrl,
    downloadUrl,
    fetchData,
    customerId,
    companyId,
    customFilter,
    filterControl,
    headerFilters,
    filterBtnRef,
    setRemoveFilter,
    noClear,
    fetchMethod,
    control,
    onEditClick,
    showAddUserButton,
    onAddUserClick,
    showDownloadButton,
    disabledDownload,
    listName,
    extraButtons,
    excelParams,
    resetFilters = false,
    ...props
}: TableProps) => {
    // localStorage key'ini belirle
    const getStorageKey = () => customFilter ? filterKey + "Custom" : filterKey;
    // MARK : States
    const [filters, setFilters] = React.useState({});
    const [rowSelectionModel, setRowSelectionModel] = useState<any>([]);
    const [filteredRows, setFilteredRows] = useState(rows);
    // localStorage'dan initial filterModel'ı yükle
    const getInitialFilterModel = () => {
        if (resetFilters) return { items: [] };

        try {
            const storedSettings = JSON.parse(localStorage.getItem(getStorageKey()) ?? '{}');
            return storedSettings?.filterModel || { items: [] };
        } catch (error) {
            return { items: [] };
        }
    };

    const [filterModel, setFilterModel] = useState<any>(getInitialFilterModel) // headerlardaki inputları temizler.
    const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
    const [sortModel, setSortModel] = useState([]);
    const [orderedColumns, setOrderedColumns] = useState(columns);
    const formattedColumns = formatColumns(columns);
    const [fetchAllData, { isLoading: allDataLoading }] = useGetAllDataMutation();

    // singleSelect kolonları için groupBy ile backend'den dinamik seçenekler (items: { key, count }[])
    const [groupByOptions, setGroupByOptions] = useState<Record<string, GroupByState>>({});
    const groupByRequestsRef = useRef<Record<string, boolean>>({});
    const currentRequestFiltersRef = useRef<any[]>([]);
    const groupByLastKeyRef = useRef<Record<string, string>>({});
    const hasInitializedFiltersRef = useRef(false);
 
    const findMenuNameByUrl = (url: string, items: any[]): string | null => {
        for (const item of items) {
            if (item.url === url) {
                return item.name;
            }
            if (item.children) {
                const result = findMenuNameByUrl(url, item.children);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    };

    /**
     * singleSelect header filter selectbox açıldığında backend'e groupBy isteği gönderir.
     * groupBy parametresi ile sadece o kolondaki mevcut değerler döner.
     */
    const fetchGroupByOptions = useCallback(
        async (field: string) => {
            try {
                const basePayload = requestBody
                    ? { ...requestBody }
                    : { filters: [], pageSize: 0, page: 1, orderBy: '', orderDesc: false };
                // GroupBy isteklerinde, açılan kolonun kendi filtresini hariç tut (eski tüm seçenekleri görebilmek için)
                const allFilters = currentRequestFiltersRef.current ?? [];
                const filtersWithoutCurrentField = allFilters.filter((f: any) => f.field !== field);

                const payload = {
                    ...basePayload,
                    groupBy: field,
                    filters: filtersWithoutCurrentField,
                    pageSize: 0,
                    page: 1,
                };
                if (customerId) payload.customerId = customerId;
                if (companyId) payload.companyId = companyId;
                if (excelParams) Object.assign(payload, excelParams);
                const method = fetchMethod || 'POST';

                // Aynı field + filtre kombinasyonu için daha önce istek atıldıysa ve sonuç elimizdeyse
                // sadece aç/kapat yaparken tekrar backend'e gitme.
                const cacheKey = JSON.stringify({
                    field,
                    filters: payload.filters,
                    customerId: payload.customerId ?? null,
                    companyId: payload.companyId ?? null,
                    listUrl,
                    method,
                });
                if (groupByLastKeyRef.current[field] === cacheKey && (groupByOptions[field]?.items?.length ?? 0) > 0) {
                    return;
                }

                if (groupByRequestsRef.current[field]) return;
                groupByRequestsRef.current[field] = true;
                setGroupByOptions((prev) => ({ ...prev, [field]: { items: prev[field]?.items ?? [], loading: true, error: null } }));

                const response: any = await fetchAllData({ url: listUrl, filter: payload, method }).unwrap();
                const arr = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : [];
                const items = arr.map((r: any) => {
                    const rawKey = r?.key !== undefined ? r.key : (r?.value !== undefined ? r.value : r?.key ?? r?.value);
                    const key = rawKey === '' ? null : rawKey;
                    return { key, count: r?.count };
                });
                groupByLastKeyRef.current[field] = cacheKey;
                setGroupByOptions((prev) => ({
                    ...prev,
                    [field]: { items, loading: false, error: null },
                }));
            } catch (err) {
                setGroupByOptions((prev) => ({
                    ...prev,
                    [field]: { items: [], loading: false, error: err },
                }));
                enqueueSnackbar('Filtre seçenekleri yüklenirken hata oluştu', { variant: 'error' });
            } finally {
                delete groupByRequestsRef.current[field];
            }
        },
        [listUrl, requestBody, fetchMethod, customerId, companyId, excelParams, fetchAllData, groupByOptions]
    );
 
    // MARK : Effects
    useEffect(() => {
        const storedFilters = localStorage.getItem(filterKey);
        const filtersObject = storedFilters
            ? JSON.parse(storedFilters)
            : {};
        if (filtersObject?.columnVisibilityModel)
            setColumnVisibilityModel((currentModel) => ({
                ...currentModel,
                ...filtersObject?.columnVisibilityModel,
            }));
    }, []);
 
    useEffect(() => {
        if (filterMode === 'client') {
            applyFilters();
        } else {
            setFilteredRows(rows);
        }
    }, [filters, rows, filterMode]);
 
    const onFilterChange = useCallback((filterModel: GridFilterModel) => {
        // Boş string (örn. tarih silindi) olan sütun filtrelerini modelden kaldır
        const items = filterModel?.items || [];
        const cleanedItems = items.filter((item: any) => {
            if (item.value === '' || item.value === null || item.value === undefined) return false;
            if (Array.isArray(item.value) && item.value.every((v: any) => String(v).trim() === '')) return false;
            if (typeof item.value === 'string' && item.value.trim() === '') return false;
            return true;
        });
        const cleanedModel: GridFilterModel = { ...filterModel, items: cleanedItems };
        let newFilters: any = {};
        setFilterModel(cleanedModel);

        // Sayfa yenilendiğinde DataGrid bazen boş filterModel ile tetikliyor; mevcut kayıtlı filtreleri silmemek için boş modeli yazma
        // Orijinal gelen item sayısına bak (temizlenmiş değil): kullanıcı tarihi silince 1 item (value: '') gelir, temizleyince 0 olur – bunu "kullanıcı filtreyi sildi" sayıp restore etmemeliyiz
        const isEmptyIncoming = !items.length;
        if (!hasInitializedFiltersRef.current && isEmptyIncoming) {
            const existing = JSON.parse(localStorage.getItem(getStorageKey()) ?? '{}');
            if (existing?.filterModel?.items?.length > 0) {
                // Grid boş gönderdi ama storage'da filtre var – kullanıcı temizlemedi, init artefact; storage'ı boşla yazma
                hasInitializedFiltersRef.current = true;
                setFilterModel(existing.filterModel);
                setFilters(
                    (existing.filterModel.items || []).reduce((acc: any, item: any) => {
                        if (item.value !== undefined && item.value !== null && item.value !== '') acc[item.field] = item.value;
                        return acc;
                    },
                    {})
                );
                const filterObj = convertTableFiltersToRequestFilter(existing.filterModel);
                handleFilterChange(filterObj);
                return;
            }
        }

        // İlk init aşaması tamamlandı; sonraki çağrılarda boş gelen model, kullanıcının filtreyi temizlediği anlamına gelir
        if (!hasInitializedFiltersRef.current) {
            hasInitializedFiltersRef.current = true;
        }
 
        const storedData = {
            filterModel: cleanedModel,
            paginationModel,
            rowSelectionModel,
            columns,
            rows,
            sortModel,
            columnVisibilityModel,
            orderedColumns
        };
 
        localStorage.setItem(getStorageKey(), JSON.stringify(storedData));
        // Önceki versiyonda filterCustom güncellenmiyordu onu güncelledik.
        // Sadece filterkey localstorage'a kaydediliyordu önceki versiyonda
 
        cleanedModel.items.forEach((item) => {
            if (item.value) {
                newFilters[item.field] = item.value;
            } else {
                delete newFilters[item.field];
            }
        });
 
        setFilters(newFilters);
 
        if (filterMode === 'client') {
            let filteredData = [...rows];
            for (const [field, value] of Object.entries(newFilters)) {
                filteredData = filteredData.filter(row =>
                    row[field] && normalizeString(row[field].toString()).includes(normalizeString(value))
                );
            }
            setFilteredRows(filteredData);
        }
 
        const filterObj = convertTableFiltersToRequestFilter(cleanedModel);
        handleFilterChange(filterObj);
    }, [handleFilterChange, rows, filterMode, filterKey, columnVisibilityModel, requestBody, columns]);
 
    const handleClear = () => {
        if (filterKey) {
            const storedData = JSON.parse(localStorage.getItem(getStorageKey()) ?? "{}");
            delete storedData.filterModel; // Sadece filtre modelini temizle
            localStorage.setItem(getStorageKey(), JSON.stringify(storedData)); // Güncellenmiş veriyi kaydet
        }
        setRowSelectionModel([]);
        localStorage.removeItem(filterKey + "-selected");
        // Burada önceki versiyonda CustomFiltre removelanıyordu onu engelledim.
        // Önce veriyi alıyorum sonra başka filtreler uygulanıyorsa onları da set ediyorum.
        if (setRemoveFilter) setRemoveFilter(true);
 
        const newFilterModel: GridFilterModel = { items: [] };
        setFilterModel(newFilterModel);
        setFilters(newFilterModel);
 
        // Pagination ve sort model'lerini de sıfırla
        const defaultPaginationModel = { page: 0, pageSize: 25 };
        setPaginationModel(defaultPaginationModel);
        setSortModel([]);
 
        // Parent component'e pagination ve sort değişikliklerini bildir
        onPaginationModelChange(defaultPaginationModel);
        sortModelChange([]);
 
        // Tüm filtreleri ve sayfalama bilgilerini sıfırla
        handleFilterChange([]);
    };
 
    const convertTableFiltersToRequestFilter = (tableFilters: any) => {
        let convertedFilters: any = [];
        const items = tableFilters?.items || [];
        items.map((item: any) => {
            const findColumn = columns.find(x => x.field === item.field);
            const isSingleSelectNull = findColumn?.type?.toLowerCase() === 'singleselect' && (item.value === null || item.value === '__null__');
            if (isSingleSelectNull) {
                // singleSelect "Boş Olanlar" seçildiğinde backend'e values: ["null"], operationType: IS_EMPTY. "Tümü" (undefined) seçiliyse filtre eklenmez.
                convertedFilters.push({
                    field: item.field,
                    logicOperator: tableFilters.logicOperator ?? "and",
                    values: ["null"],
                    valueType: RequestFilterValueTypeEnums.STRING,
                    operationType: RequestFilterOperationTypeEnum.IS_EMPTY,
                });
            } else if (item.value !== null && item.value !== undefined && item.value !== '') {
                const columnType = findColumn?.type?.toLowerCase();
                const isDateField = columnType === 'datetime' || columnType === 'date';
                let values: any[] = [];
                let operationType = mapReactMUIToFilterOperationTypes(item.operator);
                
                if (isDateField) {
                    if (item.operator === "is") {
                        operationType = 6;
                        values = [item.value];
                    } else if (item.operator === "between") {
                       
                        operationType = 13;
                        
                        if (typeof item.value === 'string') {
                            if (item.value.includes(',')) {
                                values = [item.value];
                            } else {
                                const baseDate = item.value;
                                let startDate = baseDate;
                                let endDate = baseDate;
                                
                                if (baseDate.includes('T')) {
                                    const datePart = baseDate.split('T')[0];
                                    startDate = `${datePart}T00:00:00.00`;
                                    endDate = `${datePart}T23:59:59.99`;
                                } else {
                                    startDate = `${baseDate}T00:00:00.00`;
                                    endDate = `${baseDate}T23:59:59.99`;
                                }
                                
                                values = [`${startDate},${endDate}`];
                            }
                        } else if (Array.isArray(item.value)) {
                            values = [item.value.join(',')];
                        } else {
                            values = [`${item.value},${item.value}`];
                        }
                    } else {
                        values = [item.value];
                    }
                } else {
                    // Tarih olmayan alanlar için - tüm değerleri string dizisi olarak gönder
                    if (item.operator === "between") {
                        if (Array.isArray(item.value)) {
                            values = [item.value.map((v: any) => String(v)).join(',')];
                        } else if (typeof item.value === 'string' && item.value.includes(',')) {
                            values = [item.value];
                        } else {
                            values = [String(item.value)];
                        }
                    } else if (item.operator === "equals" && typeof item.value === 'string' && item.value.includes(',')) {
                        values = [item.value.split(',')[0].trim()];
                    } else {
                        // Tüm diğer operatörler için string'e çevir
                        values = [String(item.value)];
                    }
                }
                // Boş string veya tümü boş values (örn. tarih silindi) → bu sütunun filtresini gönderme
                const allValuesEmpty = values.length === 0 || values.every((v: any) => String(v).trim() === '');
                if (allValuesEmpty) return;
                convertedFilters.push({
                    field: item.field,
                    logicOperator: tableFilters.logicOperator ?? "and",
                    values: values,
                    valueType: mapReactFilterValueTypeToRequestFilterValueType(values[0], findColumn?.type),
                    operationType: operationType,
                });
            }
        });
        return convertedFilters;
    };

    // groupBy isteğinde mevcut filtreleri kullanmak için ref güncellenir
    useEffect(() => {
        currentRequestFiltersRef.current = convertTableFiltersToRequestFilter(filterModel);
    }, [filterModel]);
 
    const handleColumnWidthChange = (params: GridColumnResizeParams) => {
        setColumnWidths((prevWidths) => {
            const updatedWidths = {
                ...prevWidths,
                [params.colDef.field]: params.width
            };
            const storedData = {
                filterModel,
                paginationModel,
                rowSelectionModel,
                columnWidths: updatedWidths,
                rows,
                sortModel,
                columnVisibilityModel,
                orderedColumns
            };
            localStorage.setItem(getStorageKey(), JSON.stringify(storedData));
            return updatedWidths;
        });
    };
 
    const handleColumnVisibilityChange = (newProps: any) => {
        if (Object.values(newProps).every((value) => value === true)) {
            setColumnVisibilityModel(newProps);
            const storedData = {
                filterModel,
                paginationModel,
                rowSelectionModel,
                columnWidths,
                rows,
                sortModel,
                columnVisibilityModel: newProps,
                orderedColumns
            };
            localStorage.setItem(getStorageKey(), JSON.stringify(storedData));
        } else {
            setColumnVisibilityModel((currentModel) => {
                const updatedModel = {
                    ...currentModel,
                    ...newProps,
                };
                const storedData = {
                    filterModel,
                    paginationModel,
                    rowSelectionModel,
                    columnWidths,
                    rows,
                    sortModel,
                    columnVisibilityModel: updatedModel,
                    orderedColumns
                };
 
                localStorage.setItem(getStorageKey(), JSON.stringify(storedData));
                return updatedModel;
            });
        }
    };
    const [isExcelExportLoading, setIsExcelExportLoading] = useState(false);
    const onDownloadAll = async () => {
        try {
            if (isExcelExportLoading) return;
            setIsExcelExportLoading(true);
            let response: any;
           
            // GET metodu kullanılıyorsa query string ile parametreleri gönder
            if (fetchMethod === "GET") {
                let queryParams = new URLSearchParams();
               
                if (excelParams) {
                    Object.keys(excelParams).forEach(key => {
                        if (excelParams[key] !== null && excelParams[key] !== undefined) {
                            queryParams.append(key, excelParams[key].toString());
                        }
                    });
                }
               
                if (customerId && !excelParams?.customerId) {
                    queryParams.append("customerId", customerId.toString());
                }
                if (companyId && !excelParams?.companyId) {
                    queryParams.append("companyId", companyId.toString());
                }
               
                const urlWithParams = `${listUrl}?${queryParams.toString()}`;
                response = await fetchAllData({
                    url: urlWithParams,
                    filter: {},
                    method: "GET"
                });
            } else {
                // POST metodu için mevcut kod
                const filterObj = convertTableFiltersToRequestFilter(filterModel);
                let payload: any = {
                    "filters": filterObj,
                    "pageSize": fetchData?.totalCount,
                    "page": 1,
                    "orderBy": "",
                    "orderDesc": true
                }
 
                if (requestBody) { //raporlar > günlük raporları indirirken kullanıyoruz.
                    payload = { ...requestBody, "pageSize": fetchData?.totalCount, "page": 1, }
                }
                if (excelParams) {
                    payload = { ...payload, ...excelParams };
                }
                if (customerId) {
                    payload = { ...payload, "customerId": customerId }
                }
                if (companyId) {
                    payload = { ...payload, "companyId": companyId };
                }
                response = await fetchAllData({
                    url: listUrl, filter: payload, method: "POST"
                });
            }
           
            if (response?.data && Array.isArray(response.data.data)) {
                // Eğer veriler response.data.data içindeyse
                downloadExcel(refactorData(columns, response.data.data, columnVisibilityModel), listName);
            } else if (response?.data && Array.isArray(response.data)) {
                // Eğer veriler doğrudan response.data içindeyse
                downloadExcel(refactorData(columns, response.data, columnVisibilityModel), listName);
            }
        } catch (error) {
            enqueueSnackbar('Veriler indirilirken hata oluştu', { variant: 'error' });
        } finally {
            setIsExcelExportLoading(false);
        }
    };
    function CustomToolbar({
        onDownload,
        onClear,
        hasActiveFilters,
        extraButtons,
        isAllDataLoading,
    }: {
        onDownload: any;
        onClear: any;
        hasActiveFilters: any;
        extraButtons?: any;
        isAllDataLoading?: boolean;
    }) {
        return (
            <GridToolbarContainer
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: { xs: '8px 8px 8px 0', sm: '8px 12px', md: '0 0 0 20px' },
                    marginLeft: { xs: 0, sm: '10px' },
                    gap: { xs: 0.5, sm: 1 },
                }}
            >
                <Box sx={{ flex: { xs: '1 1 100%', sm: 2 }, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '2px', gap: { xs: 0.5, sm: '2px' } }}>
 
 
                    {showAddUserButton && (
                        <Tooltip title="Yeni Kullanıcı" arrow>
                            <Button
                                onClick={onAddUserClick}
                                sx={{
                                    margin: '2px 3px',
                                    padding: '4px 8px',
                                    minWidth: '36px',
                                    minHeight: '32px',
                                    border: '1px solid #bdbdbd',
                                    backgroundColor: 'transparent',
                                    color: textColor,
                                    fontSize: "0.8rem !important",
                                    fontWeight: 'fontWeightBold',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                <PersonAdd sx={{
                                    width: "18px !important",
                                    height: "18px !important",
                                    marginRight: "4px",
                                    color: '#000'
                                }} />
                                <Typography sx={{ color: textColor, fontWeight: 'fontWeightBold', fontSize: "0.8rem !important" }}>
                                    Yeni Kullanıcı
                                </Typography>
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title="Filtreleri Temizle" arrow>
                        <Button sx={{
                            display: noClear ? "none" : "flex",
                            margin: '2px 3px',
                            padding: '4px 8px',
                            minWidth: '36px',
                            minHeight: '32px',
                            border: '1px solid #bdbdbd',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }} onClick={onClear} className={hasActiveFilters ? 'flashing' : ''}>
                            <Clear sx={{
                                width: "18px !important",
                                height: "18px !important",
                                marginRight: "4px",
                                color: '#000'
                            }} className={hasActiveFilters ? 'flashing' : ''} />
                            <Typography sx={{ color: textColor, fontWeight: 'fontWeightBold', fontSize: "0.8rem !important" }}>
                                Temizle
                            </Typography>
                        </Button>
                    </Tooltip>
                    {showDownloadButton && (
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState) => (
                            <React.Fragment>
                                <Button {...bindTrigger(popupState)} sx={{ margin: '2px 0px 2px 3px !important', maxWidth: "70px", width: "100%", padding: '4px 8px', minWidth: '36px', minHeight: '32px', border: '1px solid #bdbdbd', gap: "4px" }}>
                                    {(!isAllDataLoading) ? (
                                        <>
                                            <FileDownloadOutlined sx={{ width: "18px !important", height: "18px !important" }} color="primary" />
                                            <Typography sx={{ fontWeight: 'fontWeightBold', fontSize: '0.8rem !important', color: textColor }} >
                                                İndir
                                            </Typography>
                                        </>
                                    ) : (
                                        <CircularProgress sx={{ width: "22px !important", height: "22px !important", color: "#000000de" }} />
                                    )}
 
                                </Button>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem onClick={() => {
                                        onDownloadAll();
                                        popupState.close();
                                    }}>Tüm Verileri Excel'e Aktar</MenuItem>
                                    <MenuItem onClick={() => {
                                        onDownload();
                                        popupState.close();
                                    }}>Seçili Satırları Excel'e Aktar</MenuItem>
                                </Menu>
                            </React.Fragment>
                        )}
                    </PopupState>
                    )}
                    <Box sx={{
                        '& .MuiButton-root': {
                            margin: '2px 3px',
                            padding: '4px 8px',
                            minWidth: '36px',
                            minHeight: '32px',
                            border: '1px solid #bdbdbd',
                            backgroundColor: 'transparent',
                            color: textColor,
                            fontSize: "0.8rem !important",
                            fontWeight: 'fontWeightBold',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        },
                        '& .MuiButton-startIcon': {
                            marginRight: '4px',
                            '& svg': {
                                width: '18px !important',
                                height: '18px !important'
                            }
                        }
                    }}>
                        <GridToolbarColumnsButton />
                    </Box>
                    {extraButtons}
                </Box>
            </GridToolbarContainer >
        );
    }
 
 
   
    const onExcelExport = () => {
        const getSelected = JSON.parse(localStorage.getItem(filterKey + "-selected") ?? "[]") || [];
        if (rowSelectionModel.length > 0) {
            const storedSettings = JSON.parse(localStorage.getItem(filterKey) ?? '{}') || {};
            // only download selected ones
 
            const selectedData = getSelected?.filter((x: any) => rowSelectionModel.includes(x[`${idName}`]));
            downloadExcel(refactorData(columns, selectedData, columnVisibilityModel), listName);
        } else {
            enqueueSnackbar('Seçili satır bulunmamaktadır.', { variant: 'warning' });
        }
    }
 
 
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
 
    const applyFilters = (initialFilters = filters) => {
        if (Object.keys(initialFilters).length === 0) {
            setFilteredRows(rows);
        } else {
            let tempRows = [...rows];
            for (const [field, value] of Object.entries(initialFilters)) {
                tempRows = tempRows.filter(row => {
                    if (row[field]) {
                        return normalizeString(row[field].toString()).includes(normalizeString(value));
                    }
                    return false;
                });
            }
            setFilteredRows(tempRows);
        }
    };
 
    const handlePaginationModelChange = (newModel: any) => {
        setFilteredRows([]);
        setPaginationModel(newModel);
        const storedData = {
            filterModel,
            paginationModel: newModel,
            rowSelectionModel,
            columnWidths,
            sortModel,
            columnVisibilityModel,
            orderedColumns
        };
        localStorage.setItem(filterKey, JSON.stringify(storedData));
        onPaginationModelChange(newModel)
        setFilteredRows([]);
    };
 
    const handleSortModelChange = (newModel: any) => {
        setSortModel(newModel);
        const storedData = {
            filterModel,
            paginationModel,
            rowSelectionModel,
            columnWidths,
            rows,
            sortModel: newModel,
            columnVisibilityModel,
            orderedColumns
        };
        localStorage.setItem(filterKey, JSON.stringify(storedData));
        sortModelChange(newModel);
    };
 
    const [selectedCell, setSelectedCell] = React.useState<any>(null); // Seçilen hücre durumu
 
    const handleCellClick = (params: any) => {
        if (params.field === '__check__') return;
        const cellValue = getValueForClipboard(params);
        setSelectedCell({ id: params.id, field: params.field });
        navigator.clipboard.writeText(cellValue).catch(() => {
            enqueueSnackbar('Kopyalama işlemi başarısız oldu', { variant: 'error' });
        });
    };
 
    const getCellClassName = (params: any) => {
        return selectedCell?.id === params.id && selectedCell.field === params.field ? 'selected' : '';
    };
 
 
    const handleSelectionModelChange = (newSelection: any) => {
        const getSelected = JSON.parse(localStorage.getItem(filterKey + "-selected") ?? "[]");
        const result = filteredRows
            .map((innerItem: any) => {
                // Eşleşen id'yi bulursak, `innerItem`'i döndürüyoruz
                return newSelection.find((item: any) => idName && item === innerItem[idName]) ? innerItem : undefined;
            })
            .filter((item: any) => item !== undefined);
        if (getSelected.length > 0) {
            result.forEach((resultItem: any) => {
                const existingIndex = idName ? getSelected.findIndex((item: any) => item[idName] === resultItem[idName]) : -1;
                if (existingIndex !== -1) {
                    getSelected[existingIndex] = resultItem;
                } else {
                    getSelected.push(resultItem);
                }
            });
        } else {
            getSelected.push(...result);
        }
        localStorage.setItem(filterKey + "-selected", JSON.stringify(getSelected));
        const storedData = {
            filterModel,
            paginationModel,
            rowSelectionModel: newSelection,
            columnWidths,
            sortModel,
            columnVisibilityModel,
            orderedColumns
        };
        localStorage.setItem(filterKey, JSON.stringify(storedData));
        // setLocalStorageState((prev) => ({ ...prev, rowSelectionModel: newSelection }));
        setRowSelectionModel(newSelection);
    };
 
    useEffect(() => {
        // Eğer resetFilters true ise localStorage'dan filtreleri yükleme
        if (resetFilters) {
            return;
        }
       
        const storedSettings = JSON.parse(localStorage.getItem(getStorageKey()) ?? '{}');
 
        if (storedSettings?.orderedColumns?.length > 0) {
            setOrderedColumns(storedSettings?.orderedColumns);
        }
 
        setPaginationModel(storedSettings.paginationModel || { page: 0, pageSize: 25 });
        onPaginationModelChange(storedSettings.paginationModel || { page: 0, pageSize: 25 });
        setColumnWidths(storedSettings.columnWidths || {});
        setRowSelectionModel(storedSettings.rowSelectionModel || []);
        setFilters(storedSettings);
 
        // filterModel artık initial state'ten yükleniyor, burada tekrar set etmeye gerek yok
 
        if (storedSettings?.sortModel?.length > 0) {
            setSortModel(storedSettings.sortModel);
            // sıralama modelini güncelleyip tabloyu bu yeni sıralama modeline göre render ediyor.
            sortModelChange(storedSettings.sortModel);
            // üst satırda sıralama modelindeki değişiklik için çağırma yapıyor ve veriler son haline göre sıralanıyor.
        }
 
        applyFilters(storedSettings);
 
        // filterModel için handleFilterChange çağrısı artık ayrı useEffect'te yapılacak
    }, [resetFilters]); // resetFilters dependency'e eklendi

    // filterModel değiştiğinde handleFilterChange'i çağır
    useEffect(() => {
        if (filterModel?.items?.length > 0) {
            const filterObj = convertTableFiltersToRequestFilter(filterModel);
            handleFilterChange(filterObj);
        } else if (filterModel?.items?.length === 0) {
            // Filtreler temizlendiğinde boş array gönder
            handleFilterChange([]);
        }
    }, [filterModel]);

    const sortColumnsByOrder = useMemo(() => {
        if (!orderedColumns?.length) return formattedColumns;
 
        // orderedColumns'daki field sırasına göre yeni bir array oluştur
        return formattedColumns.sort((a: { field: string }, b: { field: string }) => {
            const indexA = orderedColumns.findIndex(col => col.field === a.field);
            const indexB = orderedColumns.findIndex(col => col.field === b.field);
 
            // Eğer field orderedColumns'da yoksa en sona at
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
 
            return indexA - indexB;
        });
    }, [orderedColumns, formattedColumns]);
 
    const handleColumnOrderChange = (params: any) => {
        setOrderedColumns(prevColumns => {
            // Debug için loglama
            const updatedColumns = [...prevColumns];
            const [movedColumn] = updatedColumns.splice((params.oldIndex - 1), 1);
            // Sonra direkt hedef indexe ekle
            updatedColumns.splice((params.targetIndex - 1), 0, movedColumn);
            const storedData = {
                filterModel,
                paginationModel,
                rowSelectionModel,
                columnWidths,
                sortModel,
                columnVisibilityModel,
                orderedColumns: updatedColumns
            };
            localStorage.setItem(getStorageKey(), JSON.stringify(storedData));
            return updatedColumns;
        });
    };
    const handleCellKeyDown = (params: any, event: any) => {
        const isCopy = (event.ctrlKey || event.metaKey) && event.key === 'c';
        if (!isCopy) return;
        if (params.field === '__check__') return;
        const cellContent = getValueForClipboard(params);
        setSelectedCell({ id: params.id, field: params.field });
        navigator.clipboard.writeText(cellContent).catch(() => {
            enqueueSnackbar('Kopyalama işlemi başarısız oldu', { variant: 'error' });
        });
    };
    let sx = {
        ...props.sx,
        '& .MuiInputLabel-shrink': {
            display: 'none'
        },
        '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl': {
            display: 'none'
        },
        '& .MuiDataGrid-cell': {
            fontSize: '15px !important'
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '15px !important',
            fontWeight: 600
        },
        // Tarih seçici takvim günlerinin font boyutu
        '& .MuiPickersDay-root': {
            fontSize: '0.95rem',
            fontWeight: 500
        },
        '& .MuiDayCalendar-weekDayLabel': {
            fontSize: '15px !important',
            fontWeight: 600
        },
        '& .MuiPickersCalendarHeader-label': {
            fontSize: '15px !important',
            fontWeight: 600
        }
    }
    props.sx = sx

    const gridColumns = useMemo(() => {
        return sortColumnsByOrder.map((column: any) => {
            const baseCol = { ...column, width: columnWidths[column?.field] || column?.width };
            if (column?.type === 'singleSelect' && headerFilters !== false) {
                const valueOpts = typeof column.valueOptions === 'function' ? column.valueOptions() : column.valueOptions ?? [];
                baseCol.renderHeaderFilter = (params: any) => (
                    <DynamicSingleSelectHeaderFilter
                        colDef={column}
                        item={params.item}
                        inputRef={params.inputRef}
                        valueOptions={valueOpts}
                        groupByOptions={groupByOptions}
                        fetchGroupByOptions={fetchGroupByOptions}
                    />
                );
            }
            return baseCol;
        });
    }, [sortColumnsByOrder, columnWidths, headerFilters, groupByOptions, fetchGroupByOptions]);

    const dataGridSlotProps = useMemo(
        () => ({
            loadingOverlay: {
                variant: 'skeleton' as const,
                noRowsVariant: 'skeleton' as const,
            },
        }),
        []
    );

    const dataGridSlots = useMemo(
        () => ({
            toolbar: () =>
                CustomToolbar({
                    onDownload: onExcelExport,
                    onClear: handleClear,
                    hasActiveFilters,
                    extraButtons,
                    isAllDataLoading: isExcelExportLoading,
                }),
            columnMenuIcon: Search,
            noRowsOverlay: CustomNoRowsOverlay,
            noResultsOverlay: nullComponent,
        }),
        [onExcelExport, handleClear, hasActiveFilters, extraButtons, isExcelExportLoading]
    );

    return (
        <>
            <DataGridPro
                keepNonExistentRowsSelected
                onRowSelectionModelChange={handleSelectionModelChange}
                rowSelectionModel={rowSelectionModel}
                columns={gridColumns}
                onColumnOrderChange={handleColumnOrderChange}
                slotProps={dataGridSlotProps}
                loading={isLoading}
                className='datagridpro'
                rowHeight={42}
                columnBufferPx={1500}
                rowBufferPx={100}
                onCellKeyDown={handleCellKeyDown}
                onCellClick={handleCellClick}
                getCellClassName={getCellClassName} // Hücre stilini belirlemek için ekle
                headerFilters={headerFilters === false ? false : true}
                headerFilterHeight={42}
                filterMode={'server'}
                paginationMode={'server'}// silersen hatayı yersin
                disableDensitySelector={true}
                isRowExpandable={props.isRowExpandable}
                localeText={customLocaleText}
                rows={filteredRows}
                sortingMode="server"
                pagination
                filterModel={filterModel}
                onFilterModelChange={onFilterChange}
                checkboxSelection={checkboxSelection}
                getRowId={getRowId}// burası props olarak gelmeli her bir tabloda id olmayabiliyor isim uyusmazlıgı ortaya cikiy
                getDetailPanelContent={isMasterDetailActive ? props.getDetailPanelContent : undefined}
                slots={dataGridSlots}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                sortModel={sortModel}
                paginationModel={paginationModel}
                onSortModelChange={handleSortModelChange}
                onPaginationModelChange={handlePaginationModelChange}
                onColumnWidthChange={handleColumnWidthChange}
                /* localization={{
                    locale: 'tr-TR',
                }} */
                {...props}// bu aşağıda kalmak zorunda iys row renkleri değişiyor yoksa.
 
            />
        </>
    )
}
 
export default Table;